import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, get } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Trophy, Star, TrendingUp, Users } from "lucide-react";

export default function Leaderboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tab, setTab] = useState("sellers");
  const [topSellers, setTopSellers] = useState([]);
  const [topColleges, setTopColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [usersSnap, listingsSnap] = await Promise.all([
        get(ref(db, "users")),
        get(ref(db, "listings")),
      ]);

      const listings = listingsSnap.exists()
        ? Object.entries(listingsSnap.val()).map(([id, d]) => ({ id, ...d }))
        : [];

      // Top sellers by total sales
      if (usersSnap.exists()) {
        const users = Object.entries(usersSnap.val()).map(([uid, u]) => ({ uid, ...u }));
        const salesCount = {};
        listings.filter(l => l.status === "sold").forEach(l => {
          if (l.sellerId) salesCount[l.sellerId] = (salesCount[l.sellerId] || 0) + 1;
        });
        const sellers = users
          .filter(u => salesCount[u.uid] > 0 || (u.totalRatings || 0) > 0)
          .map(u => ({ ...u, salesCount: salesCount[u.uid] || 0 }))
          .sort((a, b) => b.salesCount - a.salesCount || (b.rating || 0) - (a.rating || 0))
          .slice(0, 10);
        setTopSellers(sellers);

        // Top colleges by active listings
        const collegeStats = {};
        listings.filter(l => l.status === "active" && l.college).forEach(l => {
          if (!collegeStats[l.college]) collegeStats[l.college] = { listings: 0, members: new Set() };
          collegeStats[l.college].listings++;
          if (l.sellerId) collegeStats[l.college].members.add(l.sellerId);
        });
        const colleges = Object.entries(collegeStats)
          .map(([name, s]) => ({ name, listings: s.listings, members: s.members.size }))
          .sort((a, b) => b.listings - a.listings)
          .slice(0, 10);
        setTopColleges(colleges);
      }
      setLoading(false);
    }
    load();
  }, []);

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white border-b px-4 py-4 sticky top-14 z-10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Leaderboard</h2>
          <p className="text-xs text-gray-400">Top sellers & most active colleges</p>
        </div>
        <Trophy size={22} className="ml-auto text-yellow-500" />
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4 flex gap-2">
        <button onClick={() => setTab("sellers")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${tab === "sellers" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
          🏆 Top Sellers
        </button>
        <button onClick={() => setTab("colleges")} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${tab === "colleges" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
          🏫 Top Colleges
        </button>
      </div>

      <div className="px-4 pt-3 space-y-2 max-w-lg mx-auto">
        {loading ? (
          [1,2,3,4,5].map(i => <div key={i} className="h-16 bg-white rounded-xl animate-pulse" />)
        ) : tab === "sellers" ? (
          topSellers.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No sales data yet</div>
          ) : topSellers.map((s, i) => (
            <Link key={s.uid} to={`/seller/${s.uid}`}
              className={`bg-white rounded-xl border p-3 flex items-center gap-3 hover:shadow-sm transition ${currentUser?.uid === s.uid ? "border-blue-200 bg-blue-50" : "border-gray-100"}`}>
              <span className="text-2xl w-8 text-center flex-shrink-0">{medals[i] || `#${i+1}`}</span>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {s.photoURL
                  ? <img src={s.photoURL} alt={s.name} className="w-full h-full object-cover" />
                  : <span className="text-blue-700 font-bold">{s.name?.[0]?.toUpperCase() || "?"}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm line-clamp-1">{s.name} {currentUser?.uid === s.uid ? "(You)" : ""}</p>
                <p className="text-xs text-gray-400 truncate">{s.college || s.childSchool || "—"}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-green-600">{s.salesCount} sold</p>
                {s.rating > 0 && <p className="text-xs text-yellow-500">★ {s.rating.toFixed(1)}</p>}
              </div>
            </Link>
          ))
        ) : (
          topColleges.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No college data yet</div>
          ) : topColleges.map((c, i) => (
            <div key={c.name} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
              <span className="text-2xl w-8 text-center flex-shrink-0">{medals[i] || `#${i+1}`}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm line-clamp-1">{c.name}</p>
                <p className="text-xs text-gray-400">{c.members} active seller{c.members !== 1 ? "s" : ""}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-blue-600">{c.listings} listings</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
